import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivitiesService } from '@/api';
import type { activities_CreateActivityDTO } from '@/api';
import { presentTodayKeys } from './usePresentToday';
import { activityKeys } from './useActivities';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

/**
 * Hook to create a new activity.
 *
 * @returns TanStack Query mutation for creating an activity
 *
 * @example
 * const createActivity = useCreateActivity();
 * createActivity.mutate({
 *   child_id: 'abc123',
 *   type: 'check_in',
 *   activity_time: new Date().toISOString(),
 *   data: { mood: 'happy' },
 * });
 */
export function useCreateActivity() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (data: activities_CreateActivityDTO) =>
      ActivitiesService.postApiV1Activities(data),
    onSuccess: () => {
      // Invalidate activity lists to refetch with new data
      queryClient.invalidateQueries({ queryKey: activityKeys.lists() });
      queryClient.invalidateQueries({ queryKey: presentTodayKeys.all });

      toast.success(
        i18n._({
          id: 'activities.create.success.title',
          message: 'Activity created',
        })
      );
    },
    onError: () => {
      toast.error(
        i18n._({
          id: 'activities.create.error.title',
          message: 'Failed to create activity',
        })
      );
    },
  });
}
